import { companyStatusLabel } from '@l2u/ui/lib/companyStatus.ts';
import type { Company } from '@l2u/ui/lib/types.ts';
import { compareByVendorListName, vendorListName } from './vendorListName.ts';

/**
 * Column sorting for the Vendors list.
 *
 * Every column sorts on the text its CELL RENDERS, not on the underlying
 * column: the Vendor cell shows vendorListName's three-tier fallback, the
 * multi-value cells show a joined list, and Status shows the badge label. An
 * admin clicking a header is asking to order what they can see, and sorting the
 * raw field would disagree with the screen wherever the two differ.
 */
export type VendorSortKey = 'vendor' | 'trading' | 'type' | 'region' | 'station' | 'status';
export type VendorSortDir = 'asc' | 'desc';
export type VendorSort = { key: VendorSortKey; dir: VendorSortDir };

/** No sort params = the Vendor column, A-Z. */
export const DEFAULT_VENDOR_SORT: VendorSort = { key: 'vendor', dir: 'asc' };

const SORT_KEYS: readonly VendorSortKey[] = [
  'vendor',
  'trading',
  'type',
  'region',
  'station',
  'status',
];

/** The rendered text of each sortable cell. '' means the cell shows a dash. */
/**
 * Exactly the fields the sortable cells read. Declared rather than taking a
 * whole `Company` because the list payload no longer carries every column
 * (ADMIN_COMPANY_LIST_COLUMNS) — and because a new sortable column then cannot
 * be added without widening this type, which is where the payload allowlist
 * gets a second look.
 */
export type SortableCompany = Pick<
  Company,
  | 'abn_entity_name'
  | 'legal_entity_name'
  | 'company_name'
  | 'contractor_types'
  | 'cluster_regions'
  | 'stations'
  | 'status'
>;

const CELL_TEXT: Record<VendorSortKey, (c: SortableCompany) => string> = {
  vendor: (c) => vendorListName(c.abn_entity_name, c.legal_entity_name, c.company_name),
  trading: (c) => c.company_name,
  type: (c) => (c.contractor_types ?? []).join(', '),
  region: (c) => (c.cluster_regions ?? []).join(', '),
  station: (c) => (c.stations ?? []).join(', '),
  status: (c) => companyStatusLabel(c.status, 'admin'),
};

const collate = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * Validates the ?sort= / ?dir= pair. Both the URL and the localStorage mirror
 * are user-writable, so an unrecognised value falls back rather than being
 * trusted — the same reasoning as parseVendorsSearch. A bad direction keeps a
 * good key (the admin's column choice survives a mangled dir).
 */
export function parseVendorSort(key: unknown, dir: unknown): VendorSort {
  if (typeof key !== 'string' || !SORT_KEYS.includes(key as VendorSortKey)) {
    return DEFAULT_VENDOR_SORT;
  }
  return { key: key as VendorSortKey, dir: dir === 'desc' ? 'desc' : 'asc' };
}

export function compareVendorsBy(
  key: VendorSortKey,
  dir: VendorSortDir,
): (a: SortableCompany, b: SortableCompany) => number {
  const text = CELL_TEXT[key];
  return (a, b) => {
    const left = text(a).trim();
    const right = text(b).trim();

    // Blank cells sort LAST in BOTH directions — the one place this is not a
    // pure inversion. Three columns render "—" when unset, so reversing the
    // empties would fill the first page with dashes, which is the least useful
    // view of the list. Deliberate asymmetry, not an oversight.
    if (!left && !right) return tieBreak(a, b);
    if (!left) return 1;
    if (!right) return -1;

    const primary = collate(left, right);
    if (primary !== 0) return dir === 'desc' ? -primary : primary;
    // Equal sorted-on values: the tie-break does NOT invert with the direction.
    // The rows are indistinguishable on the chosen column, so a stable
    // secondary order beats one that flips as the admin toggles.
    return tieBreak(a, b);
  };
}

// Two rows can legitimately render the same cell text (a shared region, an
// unset station). Without a tie-break their relative order would depend on the
// engine's sort stability and the server's updated_at ordering, so it would
// shuffle as unrelated rows were edited.
//
// Reuses compareByVendorListName rather than restating it: "order by the
// displayed vendor name" should have exactly one definition, and this is also
// the list's default sort, so a divergence would be invisible until two rows
// tied.
const tieBreak = compareByVendorListName;
